import { useMemo } from "react";
import {
  SelectFieldGroupByValue,
  SelectFieldOption,
  SelectFieldPinnedOptions,
} from "../../beta/components/SelectField/types";
import { usePinnedOptions } from "./usePinnedOptions";
import { sortGroupSections, useGroupedOptions } from "./useGroupedOptions";

type UseProcessedOptionsParams = {
  options: SelectFieldOption[];
  pinned?: SelectFieldPinnedOptions;
  searchValue: string;
  groupToString?: (groupValue: SelectFieldGroupByValue) => string;
  groupSorter?: (
    a: SelectFieldGroupByValue,
    b: SelectFieldGroupByValue,
  ) => number;
};

/**
 * Shared pipeline for processing options: pinned → grouped → sorted.
 * Used by all four downshift-wrapping hooks (useCombo, useSelectMode,
 * useComboMultiple, useSelectModeMultiple).
 */
export const useProcessedOptions = ({
  options,
  pinned,
  searchValue,
  groupToString,
  groupSorter,
}: UseProcessedOptionsParams) => {
  const { pinnedSections } = usePinnedOptions(pinned, searchValue);

  const { ungroupedItems, groupSections: rawGroupSections } = useGroupedOptions(
    options,
    groupToString,
  );

  const groupSections = useMemo(() => {
    if (groupSorter && rawGroupSections.length > 0) {
      return sortGroupSections(rawGroupSections, groupSorter);
    }
    return rawGroupSections;
  }, [rawGroupSections, groupSorter]);

  return { pinnedSections, ungroupedItems, groupSections };
};
